// sapBookDate.js
// -----------------------------------------------------------------------------
// Writes the delivery date a customer booked back to the SO in SAP
// (U_KSK_BookDate), through the ASMX UpdateSoBookDate method -- so SAP, and
// anything reading ORDR beside it, can see that an order has a delivery date
// before dispatch assigns a truck. Kiosk Online Shop's order page is built on
// exactly that: `transNo` present means จัดรถแล้ว, else this field means
// จองวันส่งแล้ว.
//
// Always called AFTER the Firestore save has succeeded, and never throws: the
// booking in Firestore is what this business runs on, and SAP being down must
// not undo or block it. The caller is told whether it failed, so it can say so
// instead of implying SAP was updated. The same contract sapPin.js keeps.
//
// What to write is R.sapBookDate's decision, not this file's: the booked date
// while the booking is live, '' once it is cancelled, and nothing at all for a
// นัดหมาย that has no SO yet.
// -----------------------------------------------------------------------------
import axios from 'axios'
import R from '@/components/bookingRules'
import { reportError } from '@/components/report'

const TIMEOUT_MS = 10000

async function post (webPath, method, body) {
  const res = await axios.post(`${webPath}deliveryWebService.asmx/${method}`, body, { timeout: TIMEOUT_MS })
  if (!res || !res.data || res.data.d !== 'true') {
    throw new Error(`${method} returned ` + (res && res.data && res.data.d))
  }
}

// Returns the document numbers that were not updated -- [] when there was
// nothing to write, or when SAP took it. An RT is listed as "RT <n>" so the one
// Thai line below names which document to fix by hand.
export async function pushBookDateToSap (webPath, booking) {
  const failed = []
  let so = null
  let rt = null
  try {
    // Inside the try on purpose: every caller runs this from a block whose
    // catch reports the BOOKING as failed, so anything thrown here would tell
    // a customer their booking did not happen when it did.
    so = R.sapBookDate(booking)
    rt = R.sapRtBookDate(booking)
  } catch (e) {
    reportError('sapBookDate rule failed:', e)
    return []
  }
  await Promise.all([
    so && post(webPath, 'UpdateSoBookDate', { so: Number(so.so), bookDate: so.bookDate })
      .catch(e => { reportError(`UpdateSoBookDate failed for SO ${so.so}:`, e); failed.push(so.so) }),
    rt && post(webPath, 'UpdateRtBookDate', { rt: Number(rt.rt), bookDate: rt.bookDate })
      .catch(e => { reportError(`UpdateRtBookDate failed for RT ${rt.rt}:`, e); failed.push('RT ' + rt.rt) })
  ])
  return failed
}

// The one Thai line every page shows when SAP was not updated. `cleared` is for
// a cancellation, where the stale value left in SAP is the whole problem: SAP
// would go on showing a delivery date for a booking that no longer exists.
export function bookDateFailedNote (failed, { cleared, pooled } = {}) {
  if (!failed.length) return ''
  if (pooled) return `กลับไปรอจัดรอบแล้ว แต่ยังล้างวันส่งใน SAP ไม่สำเร็จ (${failed.map(n => /^RT /.test(n) ? n : 'SO ' + n).join(', ')}) โปรดแก้ไขใน SAP`
  return cleared
    ? `ยกเลิกในระบบแล้ว แต่ยังล้างวันส่งใน SAP ไม่สำเร็จ (${failed.map(n => /^RT /.test(n) ? n : 'SO ' + n).join(', ')}) โปรดแก้ไขใน SAP`
    : `บันทึกในระบบแล้ว แต่ยังอัปเดตวันส่งใน SAP ไม่สำเร็จ (${failed.map(n => /^RT /.test(n) ? n : 'SO ' + n).join(', ')}) โปรดแก้ไขใน SAP`
}

export default { pushBookDateToSap, bookDateFailedNote }
